
import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { RemoteReportType, ReportType } from '@/shared/types'
import { EPlaqueCard } from '@/entities/EPlaqueCard'
import { reportApi } from '@/shared/api'
import {
  useOrganizationModules,
  useReportListing,
  useUser,
} from '@/shared/composables'
import {
  getDateDistance,
  getDateFormat,
  showErrorMessage,
} from '@/shared/utils'
import { DATE_FORMAT } from '@/shared/constants'

export default defineComponent({
  name: 'PlaqueReports',

  components: {
    EPlaqueCard,
  },

  props: {
    search: String,
  },

  setup(props) {
    const { user } = useUser()
    const { isReportListingUpdated, setUpdateReportListing } =
      useReportListing()

    const route = useRoute()
    const router = useRouter()

    const reports = ref<ReportType[]>([])
    const loading = ref(false)

    const patientId = computed((): string | string[] => route.params.patientId)
    const activeReportId = computed(() => Number(route.params.reportId))
    const isPreviewMode = computed(() => route.query['view-mode'] === '1')
    const { organizationModules } = useOrganizationModules()
    const isSubscriptionEnded = computed(
      () =>
        !getDateDistance(user.value.organization.block_at) ||
        !user.value.organization.package_count ||
        organizationModules.value.indexOf('plaque') <= -1
    )

    watch(
      () => props.search,
      () => {
        getReports()
      }
    )

    watch(patientId, (newPatientId, oldPatientId) => {
      if (newPatientId && newPatientId !== oldPatientId) {
        getReports()
      }
    })

    watch(
      () => isReportListingUpdated.value,
      (isUpdated) => {
        if (isUpdated) {
          getReports()
          setUpdateReportListing(false)
        }
      }
    )

    const openFirstReport = (): void => {
      const firstReportId = reports.value[0]?.id ?? 0

      if (!firstReportId) return

      handleOpenReportPage(firstReportId)
    }

    const getReports = async (): Promise<void> => {
      loading.value = true

      reports.value = []

      if (patientId.value === 'null-patient') {
        const [error, response] = await reportApi.getReportsWithoutPatient(
          props.search
        )

        if (!error) {
          fillReportList(response.data.reports)
        }
      } else {
        const [error, response] = await reportApi.getReportList(
          Number(patientId.value)
        )

        if (!error) {
          fillReportList(response.data.reports)
          if (!route.path.includes('report')) {
            openFirstReport()
          }
        }
      }

      loading.value = false
    }

    const fillReportList = (remoteReports: RemoteReportType[]): void => {
      let previousPercentage = -1

      remoteReports.sort((a, b) => {
        if (new Date(a.date_at) > new Date(b.date_at)) {
          return -1
        } else if (new Date(a.date_at) < new Date(b.date_at)) {
          return 1
        } else {
          return 0
        }
      })

      remoteReports.forEach((report) => {
        const index = JSON.parse(report.index)

        const newReport: ReportType = {
          id: report.id,
          date_at: getDateFormat(new Date(report.date_at), DATE_FORMAT.dmyLong),
          percentage: index
            ? Math.round((index.day + index.week + index.month) * 100)
            : 0,
          status: 'default',
        }

        if (previousPercentage === -1) {
          newReport.status = 'default'
        } else {
          if (newReport.percentage > previousPercentage) {
            reports.value[reports.value.length - 1].status = 'success'
          } else if (newReport.percentage < previousPercentage) {
            reports.value[reports.value.length - 1].status = 'error'
          }
        }

        previousPercentage = newReport.percentage

        reports.value.push(newReport)
      })
    }

    const getArrowDirection = (report: ReportType): string => {
      if (report.status === 'success') {
        return '↓'
      }

      if (report.status === 'error') {
        return '↑'
      }

      return ''
    }

    const getReportPageName = async (reportId: number): Promise<string> => {
      const isOldReport = await checkIfReportIsOld(reportId)

      if (isOldReport) {
        return 'OldReportPage'
      }

      return 'PlaqueIndexReportPage'
    }

    const checkIfReportIsOld = async (reportId: number): Promise<boolean> => {
      const [error, response] = await reportApi.getReport(Number(reportId))

      if (error) {
        showErrorMessage(error)

        return false
      }

      return response.data.is_old
    }

    const handleOpenReportCreationPage = (): void => {
      router.push({
        name: 'ReportCreationPage',
        params: { patientId: patientId.value },
      })
    }

    const handleOpenReportPage = async (reportId: number): Promise<void> => {
      const reportPageName = await getReportPageName(reportId)

      if (route.query['view-mode'] === '1') {
        router.push({
          name: reportPageName,
          params: { patientId: patientId.value, reportId },
          query: { 'view-mode': 1 },
        })
      } else {
        router.push({
          name: reportPageName,
          params: { patientId: patientId.value, reportId },
        })
      }
    }

    onMounted((): void => {
      getReports()
    })

    return {
      reports,
      loading,
      activeReportId,
      isPreviewMode,
      isSubscriptionEnded,
      getArrowDirection,
      handleOpenReportCreationPage,
      handleOpenReportPage,
    }
  },
})
